import { clearDeletedMessagePreviews } from 'src/modules/shared/communications/utils/cache/funnel-conversations/deleted-message-preview.util';
import { MessageDocument } from 'src/api/generated';
import type { QueryClient } from '@tanstack/react-query';
import { recoverCanonicalConversation } from 'src/modules/shared/communications/services/canonical-conversation-recovery.service';
import {
  applyConversationDocumentPatch,
  getMessageConversationPatch,
} from 'src/modules/shared/communications/utils/cache/funnel-conversations/realtime-update.util';
import {
  applyMessageTimelineItemUpdate,
  applyTimelineItemRemoval,
} from 'src/modules/shared/communications/utils/cache/timeline-items/update.util';
import { applyMessagingMessageDetailUpdate } from 'src/modules/shared/messaging/utils/cache/messages-update.util';
import type { CommunicationsRealtimeMessagePayload } from '../../types/realtime.types';

/**
 * Applies a validated realtime message payload to owned caches.
 *
 * @param queryClient - React Query client that owns the Communications caches.
 * @param payload - Validated message realtime payload.
 */
export const applyMessageRealtimePayload = (
  queryClient: QueryClient,
  payload: CommunicationsRealtimeMessagePayload
): void => {
  if (payload.action !== 'deleted') {
    applyMessageTimelineItemUpdate({
      queryClient,
      message: payload.resource,
      action: payload.action,
      funnelId: payload.funnelId,
    });
  } else {
    applyTimelineItemRemoval({
      queryClient,
      tenantId: payload.tenantId,
      resourceId: payload.resourceId,
      resourceType: MessageDocument.modelType.MESSAGE,
      occurredAt: payload.occurredAt,
    });
  }

  applyMessagingMessageDetailUpdate({
    queryClient,
    messageId: payload.resourceId,
    message: payload.resource,
    action: payload.action,
  });

  if (payload.action === 'deleted') {
    clearDeletedMessagePreviews(queryClient);
    void recoverCanonicalConversation(
      queryClient,
      payload.funnelConversationId,
      payload.tenantId
    );
    return;
  }

  applyConversationDocumentPatch({
    queryClient,
    funnelConversationId: payload.funnelConversationId,
    action: payload.action,
    patch: getMessageConversationPatch(payload.resource),
  });
};
